import { useTheme } from 'styled-components';
import {
  Flex,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@blockanalitica/ui';
import {
  FrozenIcon,
  SupplyIcon,
  CollateralIcon,
  BorrowIcon
} from '@/components/icons';
import { Colors } from 'node_modules/@blockanalitica/ui/package/types/src/theme/colors.js';

const getMarketIconColor = (tag: string, colors: Colors) => {
  switch (tag) {
    case 'borrow_disabled':
      return colors.muted;
    case 'collateral_disabled':
      return colors.muted;
    case 'frozen':
      return colors.muted;
    case 'borrow_enabled':
      return colors.success;
    case 'collateral_enabled':
      return colors.success;
    case 'supply_enabled':
      return colors.success;
    case 'borrow_siloed':
      return colors.error;
    case 'collateral_isolation':
      return colors.warning;
    case 'supply_limit':
      return colors.error;
    default:
      return colors.muted;
  }
};

const getTooltipString = (tag: string) => {
  switch (tag) {
    case 'borrow_disabled':
      return 'Cannot be borrowed';
    case 'borrow_enabled':
      return 'Can be borrowed';
    case 'borrow_siloed':
      return 'Siloed';
    case 'collateral_disabled':
      return 'Cannot be used as collateral';
    case 'collateral_enabled':
      return 'Can be used as collateral';
    case 'collateral_isolation':
      return 'Can be used as collateral in isolation mode';
    case 'supply_enabled':
      return 'Can be supplied';
    case 'supply_limit':
      return 'Supply limit reached';
    case 'frozen':
      return 'This asset is frozen, meaning that further supply / borrow, or rate swap of these assets are unavailable. Withdrawals and debt repayments are still allowed.';
    default:
      return '';
  }
};

export default function MarketTags({
  status,
  iconSize = '2'
}: {
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
  iconSize?: string;
}) {
  const theme = useTheme();
  return (
    <Flex>
      {status['frozen'] === 'frozen' ? (
        <Tooltip>
          <TooltipTrigger>
            <FrozenIcon
              size={iconSize}
              color={getMarketIconColor(status['frozen'], theme.colors)}
            />
          </TooltipTrigger>
          <TooltipContent maxWidth="300px">
            <Flex gap="2xs" flexDirection="row" alignItems="center">
              <Flex>
                <FrozenIcon
                  size={iconSize}
                  color={getMarketIconColor(status['frozen'], theme.colors)}
                />
              </Flex>
              {getTooltipString(status['frozen'])}
            </Flex>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Flex>
          <Tooltip>
            <TooltipTrigger>
              <Flex gap="2xs">
                <SupplyIcon
                  size={iconSize}
                  color={getMarketIconColor(status.supply, theme.colors)}
                />
                <CollateralIcon
                  size={iconSize}
                  color={getMarketIconColor(status['collateral'], theme.colors)}
                />
                <BorrowIcon
                  size={iconSize}
                  color={getMarketIconColor(status['borrow'], theme.colors)}
                />
              </Flex>
            </TooltipTrigger>
            <TooltipContent>
              <Flex gap="2xs" flexDirection="column">
                <Flex gap="2xs" flexDirection="row" alignItems="center">
                  <SupplyIcon
                    size={iconSize}
                    color={getMarketIconColor(status['supply'], theme.colors)}
                  />
                  {getTooltipString(status['supply'])}
                </Flex>
                <Flex gap="2xs" flexDirection="row" alignItems="center">
                  <CollateralIcon
                    size={iconSize}
                    color={getMarketIconColor(
                      status['collateral'],
                      theme.colors
                    )}
                  />
                  {getTooltipString(status['collateral'])}
                </Flex>
                <Flex gap="2xs" flexDirection="row">
                  <BorrowIcon
                    size={iconSize}
                    color={getMarketIconColor(status['borrow'], theme.colors)}
                  />
                  {getTooltipString(status['borrow'])}
                </Flex>
              </Flex>
            </TooltipContent>
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
}
